'use client';
import { getAnonUser } from './utils';
import { getOptimizelyAttributes } from './attributes';
import Script from 'next/script';
import Cookies from 'js-cookie';
import { EventEmitter } from 'events';
import { createContext, useContext, useEffect, useState } from 'react';
import optimizelySdk, { type OptimizelyUserContext } from '@optimizely/optimizely-sdk';
import type CustomWindow from 'interfaces/Utils';
import type { PageAdConfig } from '@/app_lib/types/Ad';

declare let window: CustomWindow;

const optimizely = optimizelySdk.createInstance({
  sdkKey: process.env.NEXT_PUBLIC_OPTIMIZELY_KEY,
});

export const userFlagsEmitter = new EventEmitter();

let _cachedUser: OptimizelyUserContext | null = null;

export const getUserContext = async (pageAdConfig?: PageAdConfig) => {
  if (_cachedUser) return _cachedUser;
  const userId = Cookies.get('pianoUid') || getAnonUser();
  const attributes = await getOptimizelyAttributes(pageAdConfig);
  const user = optimizely?.createUserContext(userId, attributes) ?? null;
  if (!user) return null;

  // initialize timestamps used to compute active engagement time (milliseconds)
  try {
    const now = Date.now();
    const visible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
    // @ts-ignore - attach internal fields to user object
    (user as any).__lastTimeOnPageSentAt = now;
    (user as any).__lastActiveAt = visible ? now : 0;

    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        (user as any).__lastActiveAt = Date.now();
      } else {
        (user as any).__lastActiveAt = 0;
      }
    };

    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('visibilitychange', visibilityHandler);
      // @ts-ignore
      (user as any).__visibilityHandler = visibilityHandler;
    }

    if ((user as any).trackEvent) {
      const originalTrack = (user as any).trackEvent.bind(user);
      // @ts-ignore
      (user as any).trackEvent = async (eventKey: string, eventTags?: Record<string, any>) => {
        try {
          const nowEvent = Date.now();
          const lastSent = (user as any).__lastTimeOnPageSentAt || nowEvent;
          const wasActive = (user as any).__lastActiveAt && (user as any).__lastActiveAt > 0;
          const activeDuration = wasActive && document.visibilityState === 'visible' ? nowEvent - lastSent : 0;

          // @ts-ignore
          await originalTrack(eventKey, eventTags);
          if (activeDuration > 0) {
            try {
              // @ts-ignore
              await originalTrack('time_on_page', {
                value: activeDuration / 1000, // convert ms to seconds
                $opt_event_properties: { from_event: eventKey },
              });
            } catch {
              // ignore errors sending time_on_page
            }
          }

          // update the lastTimeOnPageSentAt timestamp regardless so interval won't duplicate
          (user as any).__lastTimeOnPageSentAt = nowEvent;
          if (document.visibilityState === 'visible') (user as any).__lastActiveAt = nowEvent;
        } catch {}
      };
    }
  } catch {
    // ignore any errors on attaching handlers
  }

  _cachedUser = user;
  return _cachedUser;
};

export interface VariantProps {
  variant?: string | null;
  enabled?: boolean;
  variables?: any;
}

export interface OptimizelyUserInfo {
  user?: OptimizelyUserContext;
  [key: string]: VariantProps | OptimizelyUserContext | undefined;
}

const currentTests = [
  {
    location: ['2025', '2024', 'article'],
    tags: [],
    excludedTags: [],
    section: [],
    excludedSections: [],
    testName: 'socialShareV2',
  },
  { location: ['section'], testName: 'section' },
  { location: ['checkout'], testName: 'helio-setup' },
  { location: ['education'], testName: 'education' },
  { location: ['2025', '2024', 'article'], testName: 'article-replatform' },
  { location: ['*'], testName: 'googleOneTap' },
  { location: ['*'], testName: 'googleOneTap-mobile' },
  { location: ['*'], testName: 'asiaMigration' },
  { location: ['*'], testName: 'crownInitalHeight' },
  { location: ['article'], testName: 'articleStickyFooter' },
  { location: ['reviews-1', 'reviews-2'], testName: 'ndrTracking' },
  { location: ['people'], testName: 'peoplePage' },
  { location: ['article', '2025', '2024'], testName: 'googleSourceButton' },
  { location: ['2025', '2024', 'article'], testName: 'desktop_right_rail' },
  {
    location: ['article', '2024', '2025'],
    primarySection: ['leadership', 'future-of-work', 'workplace-culture', 'c-suite', 'success'],
    excludedTags: ['always free', 'health evergreen', 'personal finance evergreen'],
    testName: 'pianoTemplateLocation',
  },
  { location: ['*'], testName: 'hideRecommendedVideo' },
];

interface TestConfig {
  location: string[];
  primarySection?: string[];
  tags?: string[];
  excludedTags?: string[];
  excludedSections?: string[];
  [key: string]: any;
}

const canRunTest = (test: TestConfig, pageAdConfig?: PageAdConfig) => {
  try {
    const { location, primarySection, tags, excludedTags, excludedSections } = test;
    const { ch, tags: pageTags } = pageAdConfig || {};
    const path = window.location.pathname;
    const locationInPath = location.some((loc) => path.includes(loc)) || location[0] === '*';
    const primarySectionInPageAdConfig = primarySection
      ? primarySection.some((sec) => ch?.toLowerCase() === sec) || primarySection[0] === '*'
      : true;
    const pageAdConfigTags = pageTags?.map((tag) => tag.toLowerCase()) || [];

    const tagsInPageAdConfig = tags ? tags.some((tag) => pageAdConfigTags.includes(tag)) || tags[0] === '*' : true;
    const excludedTagsNotInPageAdConfig = excludedTags
      ? !excludedTags.some((tag) => pageAdConfigTags.includes(tag))
      : true;

    const excludedSectionsNotInPageAdConfig = excludedSections
      ? !excludedSections.some((sec) => ch?.toLowerCase() === sec)
      : true;

    return (
      locationInPath &&
      primarySectionInPageAdConfig &&
      tagsInPageAdConfig &&
      excludedTagsNotInPageAdConfig &&
      excludedSectionsNotInPageAdConfig
    );
  } catch {
    return false;
  }
};

type OptimizelyContextType = {
  userFlags: OptimizelyUserInfo;
  optimizely: typeof optimizelySdk | null;
};

const OptimizelyContext = createContext<OptimizelyContextType>({
  userFlags: {},
  optimizely: null,
});

type UseOptimizelyHook = OptimizelyContextType & {
  isLoading: boolean;
};

export function useOptimizely(): UseOptimizelyHook {
  const optimizelyCtx = useContext(OptimizelyContext);
  const { userFlags } = optimizelyCtx;
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const handleUserFlagsUpdated = () => {
      setIsLoaded(true);
    };

    // Check if flags are already loaded
    if (Object.keys(userFlags).length > 0) {
      setIsLoaded(true);
    }

    userFlagsEmitter.addListener('userFlagsUpdated', handleUserFlagsUpdated);

    return () => {
      userFlagsEmitter.removeListener('userFlagsUpdated', handleUserFlagsUpdated);
    };
  }, [userFlags]);

  return {
    isLoading: !isLoaded,
    ...optimizelyCtx,
  };
}

export const OptimizelyProvider = ({
  children,
  pageAdConfig,
}: {
  children: React.ReactNode;
  pageAdConfig?: PageAdConfig;
}) => {
  const [userFlags, setUserFlags] = useState<OptimizelyUserInfo>({});
  let scrollDepth = 0;
  const scrollListener = async () => {
    const user = await getUserContext(pageAdConfig);
    const scrolled = window.scrollY + window.innerHeight;
    const totalHeight = document.documentElement.scrollHeight;
    const depth = Math.round((scrolled / totalHeight) * 100);
    if (depth > scrollDepth + 5) {
      const depthToAdd = depth - scrollDepth;
      scrollDepth = depth;
      if (user) {
        // @ts-ignore
        user.trackEvent('scroll_depth', { value: depthToAdd });
      }
    }
  };

  useEffect(() => {
    // Invalidate module-level cache when pageAdConfig changes to ensure attributes/user are correct
    _cachedUser = null;
    window.removeEventListener('scroll', scrollListener);
    window.addEventListener('beforeunload', async () => {
      const user = await getUserContext(pageAdConfig);
      if (user) {
        user.trackEvent('final_timer', { value: 1 });
      }
    });
    const calculateScrollDepth = async () => {
      window.addEventListener('scroll', scrollListener);
    };
    /*
    const addTracking = () => {
      optimizely?.notificationCenter.addNotificationListener(
        enums.NOTIFICATION_TYPES.DECISION,
        (notificationData: ListenerPayload) => {
          const { type, userId, attributes, decisionInfo } = notificationData as unknown as OnDecisionNofication;
          const trackingInfo: OptimizelyDecisionTracking = {
            attributes,
            enabled: decisionInfo.enabled,
            event: 'optimizely-decision',
            flagKey: decisionInfo.flagKey,
            ruleKey: decisionInfo.ruleKey,
            type,
            userId,
            variationKey: decisionInfo.variationKey,
          };
          window?.dbbRum?.push(['tag1', `experiment_${decisionInfo.flagKey}_${decisionInfo.variationKey}`]);
          pushToDataLayer(trackingInfo);
        },
      );

      optimizely?.notificationCenter.addNotificationListener(
        enums.NOTIFICATION_TYPES.TRACK,
        (notificationData: ListenerPayload) => {
          const { userId, attributes, eventKey, eventTags } = notificationData as unknown as TrackingPayload;
          const trackingInfo: OptimizelyTrackTracking = {
            attributes,
            event: 'optimizely-track',
            eventKey,
            userId,
            value: eventTags.$opt_event_properties.value,
          };
          const optimizelyData = window.dataLayer.filter(
            (item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision',
          );
          optimizelyData.forEach((item) => {
            const decision = item as OptimizelyDecisionTracking;
            const path = window.location.pathname;
            const currentTest = currentTests.find((test) => test.testName === decision.flagKey);
            const locationInPath =
              currentTest?.location.some((loc) => path.includes(loc)) || currentTest?.location[0] === '*';
            if (currentTest && locationInPath) {
              trackingInfo.eventExperiment = decision.flagKey;
              trackingInfo.eventExperimentId = decision.variationKey;
              pushToDataLayer(trackingInfo);
            }
          });
        },
      );
    };
*/
    void optimizely?.onReady().then(async ({ success }) => {
      if (!success || !optimizely) {
        return;
      }

      //addTracking();
      // ensure module-level cache is primed
      const user = await getUserContext(pageAdConfig);

      if (!user) return;

      const newUserFlags: OptimizelyUserInfo = { user };

      for (let i = 0; i !== currentTests.length; i += 1) {
        const { testName } = currentTests[i];

        const canRun = canRunTest(currentTests[i], pageAdConfig);

        if (!canRun) {
          newUserFlags[testName] = {
            enabled: false,
            variables: {},
            variant: null,
          };
          continue;
        }

        try {
          const decision = user.decide(testName);
          const urlParams = new URLSearchParams(window.location.search);
          const testOverride = urlParams.get(`${testName}`);
          const testOverrideBoolean = testOverride ? testOverride.toLowerCase() !== 'off' : decision.enabled;
          const testOverrideVariant = testOverride ? testOverride.toLowerCase() : decision.variationKey;

          newUserFlags[testName] = {
            enabled: testOverrideBoolean,
            variables: decision.variables,
            variant: testOverrideVariant,
          };
        } catch {
          newUserFlags[testName] = {
            enabled: false,
            variables: {},
            variant: null,
          };
        }
      }

      setUserFlags(newUserFlags);
      user.trackEvent('pages_visited', { value: 1 });
      userFlagsEmitter.emit('userFlagsUpdated', newUserFlags);
    });

    calculateScrollDepth();

    return () => {};
  }, [pageAdConfig]);
  // @ts-ignore
  return <OptimizelyContext.Provider value={{ userFlags, optimizely }}>{children}</OptimizelyContext.Provider>;
};

export const trackOptimizelyEvent = async (event: string, properties: Record<string, any>) => {
  const user = await getUserContext(window?.arcSpanPageConfig);
  if (user) {
    const tags = {
      value: properties.value || 0,
      $opt_event_properties: properties,
    };
    // @ts-ignore
    user.trackEvent(event, tags);
  }
};

export const useIntersectionTracking = (elementId: string, properties: Record<string, any>, event: string) => {
  // Keep track of which element/event combos we've already fired for so we never double-send
  // across re-renders or remounts.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const firedKey = `${elementId}::${event}`;

  // Module-level fired set ensures once-only tracking across component lifecycles
  // and multiple hook invocations.
  // @ts-ignore
  if (!(globalThis as any).__optimizely_fired_tracking_set) {
    // @ts-ignore
    (globalThis as any).__optimizely_fired_tracking_set = new Set<string>();
  }
  // @ts-ignore
  const firedSet: Set<string> = (globalThis as any).__optimizely_fired_tracking_set;

  useEffect(() => {
    if (firedSet.has(firedKey)) return; // already fired, nothing to do

    let observer: IntersectionObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const tryAttach = () => {
      const targetElement = document.getElementById(elementId);
      if (!targetElement || firedSet.has(firedKey)) return;
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              // mark as fired immediately to avoid races
              firedSet.add(firedKey);
              if (observer) observer.disconnect();

              (async () => {
                const user = await getUserContext(window?.arcSpanPageConfig);
                if ((event.includes('ad') || event.includes('stn') || event.includes('outbrain')) && user) {
                  // @ts-ignore
                  user.trackEvent('ad_impressions', { value: properties?.value || 0 });
                }
                if (user) {
                  const tags = {
                    value: properties?.value || 0,
                    $opt_event_properties: properties,
                  };
                  // @ts-ignore
                  user.trackEvent(event, tags);
                }
              })();
            }
          }
        },
        { threshold: 0.5 }, // consider element "in view" when ~half is visible
      );

      observer.observe(targetElement);
    };

    // Try to attach immediately in case element already exists
    tryAttach();

    // If element is not yet in DOM, watch for it and attach once it appears.
    if (!document.getElementById(elementId)) {
      mutationObserver = new MutationObserver(() => {
        if (document.getElementById(elementId)) {
          tryAttach();
          if (mutationObserver) mutationObserver.disconnect();
        }
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      if (observer) observer.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
    };
    // Stringify properties so shallow-equal object changes don't retrigger unnecessarily
  }, [elementId, properties, event]);
};

export const OptimizelyScript: React.FC = () => (
  <Script
    src={process.env.NEXT_PUBLIC_OPTIMIZELY_URL}
    id='optimizely'
    async
    strategy='beforeInteractive'
  />
);
